import React from "react";
import { RootAnswers } from "../../components";
import { AnswerInterface, ApiHelper, PendingCommand, MessageInterface, QuestionInterface, ArrayHelper, AnswerHelper, ToastHelper } from "../../helpers";

interface Props {
  message: MessageInterface,
  saveCallback: (messageId: number) => void,
  pendingCommand: PendingCommand,
  sendCommand?: (cmd: PendingCommand) => void
}

export const MessageAnswers: React.FC<Props> = props => {
  const [questions, setQuestions] = React.useState<QuestionInterface[]>(null);
  const [answers, setAnswers] = React.useState<AnswerInterface[]>([]);
  const [previewUpdatePending, setPreviewUpdatePending] = React.useState(false);
  const [pendingCommand, setPendingCommand] = React.useState<PendingCommand>(null);


  const init = () => { if (props.message?.templateId) loadQuestions(props.message.templateId); }


  const loadQuestions = async (templateId: number) => {
    if (previewUpdatePending) setPreviewUpdatePending(false);
    await ApiHelper.getAnonymous<QuestionInterface[]>("/questions/template/" + templateId.toString())
      .then(data => setQuestions(data || []))
      .catch(err => {
        console.error("Failed to load questions:", err);
        ToastHelper.error("Failed to load message questions");
      });
    if (props.message.id > 0) ApiHelper.getAnonymous<AnswerInterface[]>("/answers/message/" + props.message.id.toString())
      .then(data => setAnswers(data || []))
      .catch(err => console.error("Failed to load answers:", err));
    else checkCopy();
    props.sendCommand({ action: "answerLoaded", parameter: "" });

  }

  const checkCopy = () => {
    const params: any = new URLSearchParams(window.location.search);
    const copyFrom = parseInt(params.get("copyFrom"), 0);
    if (copyFrom > 0) {
      ApiHelper.getAnonymous<AnswerInterface[]>("/answers/message/" + copyFrom.toString())
        .then((data) => {
          const answers = data || [];
          answers.forEach(a => { a.id = 0; a.messageId = props.message.id; });
          setAnswers(answers)
        })
        .catch(err => console.error("Failed to copy message answers:", err));
    }
  }


  const handleSave = async (messageId: number) => {
    answers.forEach(a => a.messageId = messageId);
    await AnswerHelper.preSave(answers, questions, (percent) => {
      props.sendCommand({ action: "updateProgress", parameter: percent })
    });
    const promises: Promise<any>[] = []
    if (answers.length > 0) promises.push(ApiHelper.post("/answers", answers));
    Promise.all(promises)
      .then(() => { props.saveCallback(messageId) })
      .catch(err => {
        console.error("Failed to save message answers:", err);
        ToastHelper.error("Failed to save message answers");
      });
  }

  const handleUpdateAnswer = (answerCopy: AnswerInterface) => {
    const result = [...answers];
    const answer: AnswerInterface = ArrayHelper.getOne(result, "questionId", answerCopy.questionId);
    answer.value = answerCopy.value;
    setAnswers(result);
  }

  const handleUpdateArrayAnswer = (answerCopy: AnswerInterface, question: QuestionInterface, parentAnswer: AnswerInterface) => {
    const result = [...answers];
    const answer: AnswerInterface = ArrayHelper.getOne(result, "questionId", parentAnswer.questionId);

    let data: any;
    try {
      data = JSON.parse(answer.value);
    } catch (error) {
      console.error("Invalid JSON in MessageAnswers:", error);
      data = [{}];
    }
    data[0][question.keyName] = answerCopy.value;
    answer.value = JSON.stringify(data);
    setAnswers(result);
  }

  const handleParentCommand = () => {
    if (props.pendingCommand) {
      switch (props.pendingCommand.action) {
        case "save":
          handleSave(parseInt(props.pendingCommand.parameter as string, 0));
          break;
        case "updateAnswer":
          handleUpdateAnswer(props.pendingCommand.parameter as AnswerInterface);
          break;
        case "updateArrayAnswer":
          const param = props.pendingCommand.parameter as { answer: AnswerInterface; question: QuestionInterface; parentAnswer: AnswerInterface };
          handleUpdateArrayAnswer(param.answer, param.question, param.parentAnswer);
          break;
      }
    }
  }

  function waitASec() {
    return new Promise(resolve => {
      setTimeout(() => { resolve('') }, 1000);
    })
  }

  const triggerPreview = async () => {
    await waitASec();
    setPreviewUpdatePending(true);
  }

  const updatePreview = () => {
    if (previewUpdatePending && questions && answers) {
      const cmd: PendingCommand = { action: "updatePreview", parameter: { questions, answers } };
      //props.sendCommand(cmd);
      setPendingCommand(cmd);
      //setPreviewUpdatePending(false);
    }
  }



  React.useEffect(handleParentCommand, [props.pendingCommand]); // eslint-disable-line
  React.useEffect(init, [props.message.id || props.message.templateId]); // eslint-disable-line
  React.useEffect(() => { triggerPreview(); }, [answers, questions]); // eslint-disable-line
  React.useEffect(() => { updatePreview() }, [previewUpdatePending]); // eslint-disable-line
  React.useEffect(() => { if (pendingCommand) props.sendCommand(pendingCommand); }, [pendingCommand]); // eslint-disable-line

  return (
    <RootAnswers questions={questions} answers={answers} sendCommand={props.sendCommand} />
  );
};